import { Flag } from '@phosphor-icons/react';

import { MoneyDisplay } from '../common/MoneyDisplay';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

const ARC_FRACTION = 0.75;

/**
 * Liberation progress ring — % free is the hero, never the raw debt.
 */
export function DebtLibertyHero({
  pctPaid = 0,
  hasProgress = false,
  paidAmount = 0,
  remainingAmount = 0,
  freeEtaLabel = '',
  className,
}) {
  const pct = Math.min(100, Math.max(0, Number(pctPaid) || 0));
  const ratio = pct / 100;
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * ARC_FRACTION;
  const dash = arcLength * ratio;
  const copy = pt.liabilities.liberty;

  return (
    <section
      className={cn(
        'flex flex-col items-center gap-3 rounded-2xl border border-border bg-card px-4 py-6',
        className,
      )}
      aria-label={
        hasProgress
          ? copy.statusWithProgress.replace('{pct}', String(pct))
          : copy.statusZero
      }
    >
      <div className="relative mx-auto h-[168px] w-[168px]">
        <svg
          width="168"
          height="168"
          viewBox="0 0 168 168"
          className="-rotate-[135deg]"
          aria-hidden
        >
          <circle
            cx="84"
            cy="84"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="15"
            strokeLinecap="round"
            className="text-border"
            strokeDasharray={`${arcLength} ${circumference}`}
          />
          <circle
            cx="84"
            cy="84"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="15"
            strokeLinecap="round"
            className="text-sage-600 dark:text-sage-500"
            strokeDasharray={`${dash} ${circumference}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-2">
          {hasProgress ? (
            <>
              <p className="font-display text-[40px] font-bold leading-none tracking-tight text-sage-700 dark:text-sage-300">
                {pct}%
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {copy.freeLabel}
              </p>
            </>
          ) : (
            <p className="text-center font-display text-sm font-semibold leading-snug text-sage-700 dark:text-sage-300">
              {copy.statusZero}
            </p>
          )}
        </div>
      </div>

      {hasProgress ? (
        <p className="text-center font-display text-base font-semibold text-foreground">
          {copy.statusWithProgress.replace('{pct}', String(pct))}
        </p>
      ) : null}

      <p className="text-center text-sm text-muted-foreground">
        Já pagaste{' '}
        <span className="font-semibold tabular-nums text-foreground">
          <MoneyDisplay value={paidAmount} size="sm" />
        </span>
        {' · '}
        faltam{' '}
        <span className="font-semibold tabular-nums text-foreground">
          <MoneyDisplay value={remainingAmount} size="sm" />
        </span>
      </p>

      {freeEtaLabel ? (
        <p className="flex items-center gap-1.5 text-sm font-medium text-sage-800 dark:text-sage-300">
          <Flag className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {copy.freeEta.replace('{date}', freeEtaLabel)}
        </p>
      ) : null}
    </section>
  );
}

export default DebtLibertyHero;
